import { ERC20Token, Token } from '@pancakeswap/sdk'
import { useMemo } from 'react'
import useSWR from 'swr'
import { formatUnits } from 'viem'
import { useGetBnbBalance } from 'hooks/useTokenBalance'
import { useInfoSummary } from 'state/infoHub/hooks'
import { useTokenBalancesWithLoadingIndicator } from 'state/wallet/hooks'
import type { TokenStat } from 'server/info/types'
import { V3_API_URL, fetcher } from 'views/TradingCompetitionV3/config'
import { V4_API_URL, V4_ENABLED } from 'views/TradingCompetitionV4/config'
import { api, type Profile } from 'views/Social/api'

const WBNB = '0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c'

export interface AssetRow {
  key: string
  symbol: string
  name: string
  /** null = the native coin (BNB) */
  token: Token | null
  amount: number
  usd: number
  /** percent, only when the price source reports a real 24 h change */
  change24h: number | null
  /** USD liquidity of the token across CADINUdex pools (what a sale could draw on) */
  liquidityUSD: number
}

export interface WalletValue {
  loading: boolean
  rows: AssetRow[]
  /** sum of the priced holdings, never an unpriced one */
  usd: number
  /** the 24 h change of `usd` in dollars and percent, or null when any priced holding lacks a real 24 h price change */
  change: { usd: number; pct: number } | null
  /** holdings with a balance but no chain-checked price: listed nowhere, counted here */
  unpriced: number
  /** holdings worth more than all CADINUdex liquidity of that token: their spot value cannot be realised */
  thin: AssetRow[]
  prices: Map<string, TokenStat>
}

/**
 * Wallet value from chain-checked prices only: the tokens the CADINUdex Info summary prices from its own pools (BNB at the WBNB
 * price). A holding without such a price is never valued; the panel says how many were left out.
 */
export function useWalletValue(account?: string): WalletValue {
  const { summary, isLoading } = useInfoSummary(account ? 'cadinu' : null, 'all')
  const stats = summary?.tokens.data ?? null
  const prices = useMemo(() => new Map((stats ?? []).map((s) => [s.address.toLowerCase(), s])), [stats])
  const tokens = useMemo(() => (stats ?? []).map((s) => new ERC20Token(56, s.address as `0x${string}`, s.decimals, s.symbol, s.name)), [stats])
  const [balances, balancesLoading] = useTokenBalancesWithLoadingIndicator(account, tokens)
  const { balance: bnbWei, fetchStatus } = useGetBnbBalance()

  return useMemo(() => {
    const rows: AssetRow[] = []
    let unpriced = 0
    const wbnb = prices.get(WBNB)
    const bnb = Number(formatUnits(bnbWei, 18))
    if (bnb > 0) {
      if (wbnb?.priceUSD) rows.push({ key: 'BNB', symbol: 'BNB', name: 'BNB', token: null, amount: bnb, usd: bnb * wbnb.priceUSD, change24h: wbnb.priceChange24h, liquidityUSD: Infinity })
      else unpriced += 1
    }
    for (const t of tokens) {
      const amount = Number(balances[t.address]?.toExact() ?? 0)
      if (!(amount > 0)) continue
      const s = prices.get(t.address.toLowerCase())
      if (!s?.priceUSD) {
        unpriced += 1
        continue
      }
      rows.push({ key: t.address, symbol: t.symbol ?? '', name: t.name ?? '', token: t, amount, usd: amount * s.priceUSD, change24h: s.priceChange24h, liquidityUSD: s.liquidityUSD ?? 0 })
    }
    rows.sort((a, b) => b.usd - a.usd)
    const usd = rows.reduce((sum, r) => sum + r.usd, 0)
    const valued = rows.filter((r) => r.usd > 0)
    let change: WalletValue['change'] = null
    if (valued.length > 0 && valued.every((r) => r.change24h !== null)) {
      const before = valued.reduce((sum, r) => sum + r.usd / (1 + (r.change24h as number) / 100), 0)
      if (before > 0) change = { usd: usd - before, pct: ((usd - before) / before) * 100 }
    }
    // stablecoins and BNB trade far beyond our pools; only the tokens priced from our own thin pools can exceed what those pools hold
    const thin = rows.filter((r) => r.token && r.usd > r.liquidityUSD && !['USDT', 'USDC', 'BUSD', 'WBNB'].includes(r.symbol))
    return { loading: isLoading || balancesLoading || fetchStatus === 'loading', rows, usd, change, unpriced, thin, prices }
  }, [prices, tokens, balances, bnbWei, isLoading, balancesLoading, fetchStatus])
}

type Claimable = { id: number; rewardToken: string; amount: string }

/** Prizes the wallet can claim from "Your competitions" (current contract and, with V4 on, the legacy one), valued with the same prices. */
export function useCompetitionRewards(account: string | undefined, prices: Map<string, TokenStat>) {
  const a = account?.toLowerCase()
  const opts = { refreshInterval: 60_000, shouldRetryOnError: false, revalidateOnFocus: false }
  const cur = useSWR<{ claimable?: Claimable[] }>(a ? `${V4_ENABLED ? V4_API_URL : V3_API_URL}/user/${a}` : null, fetcher, opts)
  const old = useSWR<{ claimable?: Claimable[] }>(a && V4_ENABLED ? `${V3_API_URL}/user/${a}` : null, fetcher, opts)
  return useMemo(() => {
    const list = [...(cur.data?.claimable ?? []), ...(old.data?.claimable ?? [])]
    let usd = 0
    let unpriced = 0
    for (const c of list) {
      const s = prices.get(c.rewardToken.toLowerCase())
      if (!s?.priceUSD) {
        unpriced += 1
        continue
      }
      usd += Number(formatUnits(BigInt(c.amount), s.decimals)) * s.priceUSD
    }
    // V3 + V4 summed; if either contract's API fails the figure would be short, so it is reported as not loaded instead
    const failed = Boolean(cur.error && !cur.data) || (V4_ENABLED && Boolean(old.error && !old.data))
    return { count: list.length, usd: failed ? 0 : usd, unpriced, loading: cur.isLoading || old.isLoading, failed }
  }, [cur.data, old.data, cur.isLoading, old.isLoading, cur.error, old.error, prices])
}

/** The social profile of an address (public endpoint, no sign-in needed): handle, avatar NFT and its level. */
export function useSocialIdentity(account?: string) {
  const { data } = useSWR<Profile>(account ? `wallet-panel:social:${account.toLowerCase()}` : null, () => api<Profile>(`profiles/${account}`), {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
  })
  return data ?? null
}
